import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

function StarRating({ value, size = '1rem' }) {
  return (
    <span>
      {[1, 2, 3, 4, 5].map((s) => (
        <span key={s} style={{ color: s <= Math.round(value) ? '#f59e0b' : '#e2e8f0', fontSize: size }}>★</span>
      ))}
      <span style={{ marginLeft: 5, fontSize: '0.82rem', color: '#64748b', fontWeight: 600 }}>
        {value ? value.toFixed(1) : '—'}
      </span>
    </span>
  );
}

function MiniDistrib({ dist, total }) {
  return (
    <div style={{ marginTop: 6 }}>
      {[5, 4, 3, 2, 1].map((star) => {
        const count = dist[star] || 0;
        const pct = total ? Math.round((count / total) * 100) : 0;
        return (
          <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
            <span style={{ fontSize: '0.68rem', color: '#94a3b8', width: 8 }}>{star}</span>
            <span style={{ color: '#f59e0b', fontSize: '0.68rem' }}>★</span>
            <div style={{ flex: 1, height: 5, background: '#f1f5f9', borderRadius: 3 }}>
              <div style={{ width: `${pct}%`, height: '100%', background: '#f59e0b', borderRadius: 3 }} />
            </div>
            <span style={{ fontSize: '0.68rem', color: '#94a3b8', width: 20, textAlign: 'right' }}>{count}</span>
          </div>
        );
      })}
    </div>
  );
}

function computeStats(data) {
  if (!data || data.length === 0) return null;

  const rated = data.filter((f) => f.rating != null);
  const skipped = data.filter((f) => f.skipped);
  const avg = rated.length ? rated.reduce((s, f) => s + f.rating, 0) / rated.length : null;

  // Per question, with breakdown by drink
  const byQuestion = {};
  rated.forEach((f) => {
    const q = f.question || 'Sans question';
    if (!byQuestion[q]) {
      byQuestion[q] = {
        total: 0,
        sum: 0,
        dist: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
        byDrink: {} // Add drink breakdown per question
      };
    }
    byQuestion[q].total++;
    byQuestion[q].sum += f.rating;
    byQuestion[q].dist[f.rating]++;

    // Track per drink within this question
    const drink = f.drink_name;
    if (!byQuestion[q].byDrink[drink]) {
      byQuestion[q].byDrink[drink] = { total: 0, sum: 0, dist: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } };
    }
    byQuestion[q].byDrink[drink].total++;
    byQuestion[q].byDrink[drink].sum += f.rating;
    byQuestion[q].byDrink[drink].dist[f.rating]++;
  });

  Object.values(byQuestion).forEach((q) => {
    q.avg_rating = q.sum / q.total;
    Object.values(q.byDrink).forEach((d) => { d.avg_rating = d.sum / d.total; });
  });

  // Overall dist
  const dist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  rated.forEach((f) => { dist[f.rating]++; });

  // All unique drinks
  const allDrinks = [...new Set(rated.map(f => f.drink_name))];

  const comments = data.filter((f) => f.comment).slice(0, 5);

  return { total: data.length, rated: rated.length, skipped: skipped.length, avg_rating: avg, skip_rate: (skipped.length / data.length) * 100, byQuestion, dist, comments, allDrinks };
}

const card = {
  background: '#f8fafc', borderRadius: 12, padding: '1rem',
};

export default function FeedbackPanel({ distributeurId, distributeurName, accentColor = '#1e293b' }) {
  const [feedback, setFeedback] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [days, setDays] = useState(30);
  const [drinkFilters, setDrinkFilters] = useState({}); // { questionName: selectedDrink }

  const loadFeedback = useCallback(() => {
    const since = new Date();
    since.setDate(since.getDate() - days);
    supabase
      .from('feedback')
      .select('drink_name, rating, skipped, comment, question, timestamp')
      .eq('distributeur_id', distributeurId)
      .gte('timestamp', since.toISOString())
      .order('timestamp', { ascending: false })
      .then(({ data }) => {
        setRawData(data || []);
        setFeedback(computeStats(data));
      });
  }, [distributeurId, days]);

  useEffect(() => { loadFeedback(); }, [loadFeedback]);

  useEffect(() => {
    const channel = supabase
      .channel(`feedback-${distributeurId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'feedback', filter: `distributeur_id=eq.${distributeurId}` },
        () => loadFeedback())
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [distributeurId, loadFeedback]);

  const handleDrinkFilterChange = (question, drink) => {
    setDrinkFilters(prev => ({ ...prev, [question]: drink }));
  };

  const exportToCSV = () => {
    if (!rawData || rawData.length === 0) {
      alert('Aucune donnée à exporter');
      return;
    }

    // Define CSV headers
    const headers = ['Distributeur ID', 'Boisson', 'Note', 'Ignoré', 'Question', 'Commentaire', 'Date'];

    // Convert data to CSV rows
    const rows = rawData.map(row => [
      distributeurId,
      row.drink_name || '',
      row.rating !== null ? row.rating : '',
      row.skipped ? 'Oui' : 'Non',
      row.question || '',
      row.comment ? `"${row.comment.replace(/"/g, '""')}"` : '', // Escape quotes in comments
      new Date(row.timestamp).toLocaleString('fr-FR')
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' }); // Add BOM for Excel
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `feedback_${distributeurName || distributeurId}_${days}j_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{ background: '#fff', borderRadius: '0 16px 16px 16px', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: `2px solid ${accentColor}`, borderTop: `4px solid ${accentColor}` }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>
          Feedback — {distributeurName || distributeurId}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            onClick={exportToCSV}
            style={{
              padding: '0.35rem 0.9rem',
              fontSize: '0.82rem',
              border: '1px solid #22c55e',
              borderRadius: 8,
              color: '#22c55e',
              background: '#fff',
              cursor: 'pointer',
              fontWeight: 600,
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#22c55e';
              e.currentTarget.style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.color = '#22c55e';
            }}
          >
            📊 Exporter CSV
          </button>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            style={{ padding: '0.35rem 0.7rem', fontSize: '0.82rem', border: '1px solid #e2e8f0', borderRadius: 8, color: '#374151', outline: 'none' }}>
            <option value={7}>7 jours</option>
            <option value={30}>30 jours</option>
            <option value={90}>90 jours</option>
          </select>
        </div>
      </div>

      {!feedback ? (
        <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0 }}>Aucun feedback sur cette période</p>
      ) : (
        <>
          {/* KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1.25rem' }}>
            {[
              { label: 'Note moyenne', value: feedback.avg_rating ? feedback.avg_rating.toFixed(1) : '—', sub: <StarRating value={feedback.avg_rating} size="0.9rem" />, color: '#f59e0b' },
              { label: 'Total avis', value: feedback.total, sub: `${feedback.rated} notés`, color: '#3b82f6' },
              { label: 'Ignorés', value: feedback.skipped, sub: `${Math.round(feedback.skip_rate)}% skip rate`, color: '#f97316' },
              { label: 'Questions', value: Object.keys(feedback.byQuestion).length, sub: 'types de questions', color: '#8b5cf6' },
            ].map(({ label, value, sub, color }) => (
              <div key={label} style={{ background: '#f8fafc', borderRadius: 12, padding: '0.9rem 1rem', borderLeft: `3px solid ${color}` }}>
                <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>{value}</div>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 }}>{label}</div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 4 }}>{sub}</div>
              </div>
            ))}
          </div>

          {/* Questions avec filtre par boisson */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
            {Object.entries(feedback.byQuestion)
              .sort((a, b) => b[1].avg_rating - a[1].avg_rating)
              .map(([question, questionData]) => {
                const selectedDrink = drinkFilters[question] || 'all';
                const displayData = selectedDrink === 'all' ? questionData : questionData.byDrink[selectedDrink];

                return (
                  <div key={question} style={card}>
                    {/* Question title */}
                    <p style={{ margin: '0 0 8px', fontSize: '0.9rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.3 }}>
                      {question}
                    </p>

                    {/* Drink filter dropdown */}
                    <select
                      value={selectedDrink}
                      onChange={(e) => handleDrinkFilterChange(question, e.target.value)}
                      style={{
                        padding: '0.35rem 0.5rem',
                        fontSize: '0.75rem',
                        border: '1px solid #e2e8f0',
                        borderRadius: 6,
                        color: '#374151',
                        outline: 'none',
                        marginBottom: '8px',
                        width: '100%',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="all">Toutes les boissons</option>
                      {Object.keys(questionData.byDrink).map(drink => (
                        <option key={drink} value={drink}>{drink}</option>
                      ))}
                    </select>

                    {/* Stats */}
                    {displayData && (
                      <>
                        <StarRating value={displayData.avg_rating} />
                        <span style={{ marginLeft: 8, fontSize: '0.75rem', color: '#94a3b8' }}>
                          {displayData.total} réponse{displayData.total > 1 ? 's' : ''}
                        </span>
                        <MiniDistrib dist={displayData.dist} total={displayData.total} />
                      </>
                    )}
                  </div>
                );
              })}
          </div>

          {/* Commentaires */}
          {feedback.comments.length > 0 && (
            <>
              <h3 style={{ margin: '1.5rem 0 0.75rem', fontSize: '0.95rem', fontWeight: 700, color: '#1e293b' }}>
                Commentaires récents
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {feedback.comments.map((f, i) => (
                  <div key={i} style={{ ...card, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <StarRating value={f.rating} size="0.85rem" />
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: '#374151' }}>{f.comment}</p>
                      {f.question && <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>↳ {f.question}</p>}
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>{new Date(f.timestamp).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
